/**
 * Die Adressen der Anwendung.
 *
 * Bis hierher lebten Bereich und Anlage in einem `useState`: Ein Neuladen landete
 * wieder auf der Übersicht der ersten Anlage, kein Bereich war verlinkbar, und die
 * Zurück-Taste verließ die Anwendung. Seither steht beides im Pfad.
 *
 * **Kein Router-Paket, sondern die History-API.** Es gibt sieben Adressen und eine
 * Verzweigung; eine Bibliothek dafür wäre mehr Vertrag als Nutzen, und jede
 * Abhängigkeit kostet eine Lizenzprüfung (Regel 5). Mit HM-010 kommen der
 * Zeitraum-Wähler und tiefere Ansichten — spätestens dann lohnt TanStack Router.
 * Die Adressen bleiben dabei dieselben, weil sie hier an genau einer Stelle
 * gebildet und gelesen werden.
 *
 * **Die Anlage steht im Pfad, nicht in einem Speicher.** `/anlagen/{id}/…` ist der
 * Grund, warum ein Neuladen dieselbe Anlage zeigt und warum ein Link auf „die
 * Geräte dieses Hauses" verweisen kann statt auf „die Geräte, die gerade gewählt
 * sind". An einem **Konto**-Bereich hängt sie als `?anlage=` — der Umschalter steht
 * auch über dem Profil, und ohne den Parameter zeigte er nach einem Neuladen wieder
 * die erste Anlage statt der gewählten.
 *
 * Die Pfade sind deutsch wie die Oberfläche. Sie sind Teil dessen, was der Nutzer
 * liest, und ein `/settings` unter einer durchweg deutschen Anwendung ist eine
 * Naht, die man sieht.
 */
import { useSyncExternalStore } from "react";
import { CLAIM_PATH, parseClaimCode } from "./claim-link.js";

/** Die Bereiche der Anlage — sie stehen in der Seitenleiste. */
export type SiteSection =
  | "electricity"
  | "charging"
  | "meters"
  | "units"
  | "generation"
  | "concept"
  | "devices"
  | "settings";
/**
 * Die Reiter an einer Wohneinheit (HM-079, HM-080): „Strom" mit den Werten aus
 * dem Messkonzept, die Bewohner und die Einstellungen — Stammdaten und die
 * Messstellen der Einheit. **„Strom" steht ohne Slug** (HM-156): Seit die
 * Übersicht entfallen ist, ist die Sparte der Anfang, und
 * `/anlagen/{id}/wohneinheiten/{unitId}` — die Adresse, die die Liste seit
 * HM-079 bildet — führt dorthin. Das alte `…/strom` wird gedeutet und
 * umgeschrieben.
 */
export type UnitTab = "electricity" | "residents" | "settings";
/**
 * Die Ansichten einer Wohnung aus Sicht des Bewohners (HM-083). **Die
 * Übersicht ist entfallen** (HM-156): Umgesetzt ist eine Sparte, und eine
 * Übersicht über eine Sparte war dieselbe Seite noch einmal, knapper. `/wohnung/{id}`
 * ist seither „Strom"; weitere Sparten kommen als weitere Werte hinzu, und
 * mit der zweiten lohnt wieder eine Übersicht.
 */
export type UnitSection = "electricity" | "charging";
/**
 * Die Reiter der Gerätedetailseite.
 *
 * **Ein Reiter ist eine Ansicht und gehört deshalb in die Adresse.** Der
 * Entwurf hält ihn in einem `useState`; damit wäre die Konfiguration eines
 * Geräts nicht verlinkbar, ein Neuladen nach dem Absenden landete wieder auf
 * der Übersicht, und die Zurück-Taste ginge an drei Vierteln der Seite vorbei.
 * Das ist genau der Zustand, aus dem HM-037 die Anwendung herausgeholt hat.
 */
export type DeviceTab = "overview" | "control" | "settings";
/**
 * Die Reiter an einem Zähler (HM-068).
 *
 * **Zwei Ansichten statt einer langen Seite.** Die Übersicht beantwortet die
 * Frage, mit der man einen Zähler öffnet — was steht drauf, wie lief es
 * zuletzt. Alles, was man einmal einrichtet und danach jahrelang nicht mehr
 * anfasst — Quellbindungen, Stammdaten, Archivieren —, liegt unter
 * „Einstellungen".
 *
 * **Und sie stehen in der Adresse, nicht in einem `useState`** — dieselbe
 * Begründung wie beim Gerät (HM-042): Sonst wäre die Einrichtung eines Zählers
 * nicht verlinkbar, ein Neuladen nach dem Binden landete auf der Übersicht, und
 * die Zurück-Taste ginge an der halben Seite vorbei.
 */
export type MeterTab = "overview" | "settings";
/**
 * Die Zeitfenster des Datenpunkt-Verlaufs.
 *
 * **Rollierende Fenster, keine Kalenderzeiträume.** „24 Stunden" heißt die
 * letzten 24 Stunden und nicht „heute"; „30 Tage" sind 30 mal 24 Stunden. Das
 * ist die ehrliche Beschreibung dessen, was die Rohschicht hergibt — ein
 * Kalendertag verlangt die Zeitzone der Anlage und eine Tagesgrenze, die 23
 * oder 25 Stunden lang sein kann, und die gibt es hier nicht zu gewinnen. Wer
 * Tage vergleichen will, bekommt sie mit den Zählwerksreihen (HM-008, HM-010).
 */
export const HISTORY_RANGES = ["24h", "7d", "30d"] as const;
export type HistoryRange = (typeof HISTORY_RANGES)[number];

/**
 * Die Kalenderzeiträume von „Strom & Erzeugung" (HM-076) — die Werte des
 * Vertrags; die Adresse trägt sie deutsch (`tag`, `woche`, `monat`, `jahr`).
 */
export const ENERGY_PERIODS = ["day", "week", "month", "year"] as const;
export type EnergyPeriod = (typeof ENERGY_PERIODS)[number];

/**
 * Die drei Rechtsdokumente (HM-061, OQ-033).
 *
 * **Sie sind kein `Area`.** Ein Bereich gehört entweder der Anlage oder dem
 * Konto — das ist der Schnitt, nach dem die Seitenleiste gebaut ist (HM-031).
 * Ein Impressum gehört keinem von beiden: Es ändert sich nicht, wenn man oben
 * die Anlage wechselt, und es steht auch jemandem zu, der gar kein Konto hat.
 * Als dritter `Area`-Wert müsste es in beiden Menüs unterdrückt werden.
 */
export type LegalDocument = "imprint" | "privacy" | "terms";

/**
 * Die Zeiträume der Nutzungsstatistik (HM-168) — rollierend in Tagen bis
 * einschließlich heute, wie `USAGE_REPORT_DAYS` im Vertrag.
 */
export const USAGE_DAYS = [7, 30, 90, 365] as const;
export type UsageDays = (typeof USAGE_DAYS)[number];
export const USAGE_PLATFORM_FILTERS = ["web", "android", "ios"] as const;
export type UsagePlatformFilter = (typeof USAGE_PLATFORM_FILTERS)[number];
/**
 * Die Reiter der Nutzungsstatistik (HM-174). Der Überblick steht ohne Slug, damit
 * `/admin/nutzung` bleibt, was es war; die anderen sind Pfadsegmente wie die Reiter
 * eines Geräts — „sieh dir die Zeiten an" ist ein Link.
 */
export const USAGE_TABS = ["overview", "times", "screens", "actions", "platforms"] as const;
export type UsageTab = (typeof USAGE_TABS)[number];
/** Tage oder Wochen im Verlauf des Überblicks — eine Einstellung, deshalb ein Suchparameter. */
export type UsageGrain = "day" | "week";

/**
 * Die Filter der Feedback-Liste (HM-169) — Art und Status, wie im Vertrag.
 * In der Adresse stehen sie deutsch (`?art=fehler&status=offen`).
 */
export const FEEDBACK_KIND_FILTERS = ["bug", "wish"] as const;
export type FeedbackKindFilter = (typeof FEEDBACK_KIND_FILTERS)[number];
export const FEEDBACK_STATUS_FILTERS = ["open", "accepted", "done", "rejected"] as const;
export type FeedbackStatusFilter = (typeof FEEDBACK_STATUS_FILTERS)[number];

/**
 * Die Bereiche des Kontos — Profil und Anlagenliste hängen am Menü am Fuß der
 * Leiste bzw. am Umschalter, die Bereiche des Plattform-Admins am Admin-Bereich.
 */
export type AccountArea = "profile" | "sites" | AdminArea;
/**
 * **Die Bereiche des Plattform-Admins** — sie gehören weder der Anlage noch dem
 * eigenen Konto, sondern der Installation. Seit es vier davon gibt, stehen sie
 * nicht mehr einzeln im Kontomenü, sondern unter `/admin/…` mit einer eigenen
 * Seitenleiste; das Kontomenü führt nur noch „Administration" dorthin.
 */
/** `fleet` sind alle Geräte der Plattform (#89) — `devices` ist die Geräteverwaltung der Anlage. */
export type AdminArea = "system" | "admin" | "fleet" | "usage" | "feedback";
export type Area = SiteSection | AccountArea;

/**
 * Wohin die Adresse zeigt.
 *
 * `start` ist keine eigene Ansicht, sondern „noch nicht entschieden": Der Rahmen
 * schreibt daraus eine richtige Adresse, sobald er die Anlagen kennt. Nur er weiß,
 * welche es gibt.
 */
export type Route =
  | { readonly kind: "start" }
  /**
   * Die Anlagen-Bereiche **ohne** eigenen Zeitraum.
   *
   * **„Strom & Erzeugung" und „Laden" fehlen hier absichtlich.** Beide tragen
   * eigene Parameter und haben deshalb eine eigene Variante; stünden sie
   * zusätzlich hier, gäbe es zwei Darstellungen derselben Adresse, und die eine
   * ließe den Zeitraum still fallen. Der Typ macht das unmöglich statt eine
   * Prüfung daraus.
   */
  | {
      readonly kind: "site";
      readonly siteId: string;
      readonly area: Exclude<SiteSection, "electricity" | "charging">;
    }
  /**
   * **Laden** — `/anlagen/{id}/laden?ladepunkt=` (HM-119). Eine eigene Variante,
   * weil eine Anlage mehrere Ladepunkte haben kann — im Mieterstrom einen je
   * Stellplatz — und „der Ladepunkt in der Tiefgarage" ein Link sein soll und
   * keine Wegbeschreibung. `null` heißt „der erste".
   */
  | {
      readonly kind: "charging";
      readonly siteId: string;
      readonly chargePointId: string | null;
    }
  /**
   * Strom & Erzeugung mit Kalenderzeitraum — `/anlagen/{id}/strom?zeitraum=&datum=`
   * (HM-012, HM-076) — **die Startseite einer Anlage**, seit die Übersicht
   * entfallen ist (HM-156). `date` ist ein Kalenderdatum der Anlage, kein Zeitpunkt —
   * `null` heißt „der aktuelle Zeitraum", und den löst der Server in der
   * Zeitzone der Anlage auf; die Oberfläche kennt deren Mitternacht nicht.
   *
   * **Eine eigene Variante und kein `range` am Anlagen-Bereich.** Ein Feld,
   * das nur bei `area === "electricity"` etwas bedeuten darf, ließe sich an
   * jedem anderen Bereich mitschreiben, ohne dass es auffiele — dieselbe
   * Überlegung wie beim Gerät. Der Zeitraum steht als Suchparameter, weil er
   * die Ansicht **einstellt** und nicht bestimmt, was sie zeigt; und er steht
   * überhaupt in der Adresse, weil „die letzte Woche dieses Hauses" ein Link
   * ist oder eine Wegbeschreibung (HM-053, HM-066).
   */
  | {
      readonly kind: "electricity";
      readonly siteId: string;
      readonly period: EnergyPeriod;
      readonly date: string | null;
    }
  /**
   * Ein einzelnes Gerät — `/anlagen/{id}/geraete/{deviceId}`.
   *
   * **Eine eigene Variante und kein `deviceId` am Anlagen-Bereich.** Ein Feld,
   * das nur bei `area === "devices"` etwas bedeuten darf, ließe sich überall
   * sonst mitschreiben, ohne dass es auffiele. Hier trägt die Form die Regel:
   * Es gibt kein Gerät ohne Geräteverwaltung darüber.
   */
  | {
      readonly kind: "device";
      readonly siteId: string;
      readonly deviceId: string;
      readonly tab: DeviceTab;
    }
  /**
   * Ein einzelner **Datenpunkt** und sein Verlauf (HM-053) —
   * `/anlagen/{id}/geraete/{deviceId}/datenpunkte/{datapointId}`.
   *
   * **Eine Ebene unter dem Reiter, aus dem sie kommt.** Der Pfad sagt damit
   * auch, wohin „zurück" führt: auf die Liste der Datenpunkte dieses Geräts.
   *
   * **Der Zeitraum hängt als `?zeitraum=` daran** und nicht in einem `useState`.
   * Es ist dieselbe Begründung wie beim Reiter (HM-042): Wer jemandem „die
   * Spitze von letzter Woche" zeigen will, schickt einen Link, und ein Neuladen
   * springt sonst auf 24 Stunden zurück. Er steht als Suchparameter und nicht im
   * Pfad, weil er die Ansicht **einstellt** und nicht bestimmt, was sie zeigt.
   */
  | {
      readonly kind: "datapoint";
      readonly siteId: string;
      readonly deviceId: string;
      readonly datapointId: string;
      readonly range: HistoryRange;
    }
  /**
   * Ein einzelner **Zähler** — `/anlagen/{id}/zaehler/{meterId}` (HM-057).
   *
   * Dieselbe Form wie beim Gerät, aus demselben Grund: Ein Feld, das nur bei
   * `area === "meters"` etwas bedeuten darf, ließe sich überall sonst
   * mitschreiben, ohne dass es auffiele.
   *
   * **Ein Reiter steht im Pfad** (HM-068): `/…/{meterId}` ist die Übersicht,
   * `/…/{meterId}/einstellungen` die Einrichtung. Er bestimmt, *was* die Seite
   * zeigt, und gehört deshalb in den Pfad — anders als die beiden Parameter
   * darunter, die sie nur **einstellen**.
   *
   * **Zwei Suchparameter stellen die Ansicht ein** (HM-066): welches Zählwerk
   * die Kacheln und das Diagramm zeigen (`?zaehlwerk=`) und über welchen
   * Zeitraum (`?zeitraum=`). Beides gehört in die Adresse und nicht in ein
   * `useState` — dieselbe Begründung wie beim Reiter eines Geräts (HM-042) und
   * beim Zeitraum eines Datenpunkts (HM-053): Ein Zweirichtungszähler führt
   * Bezug und Einspeisung getrennt, und „sieh dir die Einspeisung der letzten
   * Woche an" ist ein Link oder es ist eine Wegbeschreibung.
   *
   * **`register` ist `null` für „das erste".** Nicht die Kennung des ersten
   * Zählwerks: Die kennt erst, wer den Zähler geladen hat, und ein Link auf ein
   * Zählwerk, das es an diesem Zähler nicht gibt, fiele sonst auf eine leere
   * Ansicht statt auf die übliche.
   */
  | {
      readonly kind: "meter";
      readonly siteId: string;
      readonly meterId: string;
      readonly tab: MeterTab;
      readonly register: string | null;
      readonly range: HistoryRange;
    }
  /**
   * Eine einzelne **Erzeugungsanlage** — `/anlagen/{id}/erzeugungsanlagen/{assetId}`
   * (HM-131). Dieselbe Form wie Zähler und Gerät; der Zeitraum ihres Verlaufs
   * hängt als `?zeitraum=` daran, wie am Datenpunkt — er stellt die Ansicht
   * ein und bestimmt nicht, was sie zeigt.
   */
  | {
      readonly kind: "generation-asset";
      readonly siteId: string;
      readonly assetId: string;
      readonly range: HistoryRange;
    }
  /**
   * Eine einzelne **Wohneinheit** — `/anlagen/{id}/wohneinheiten/{unitId}` (HM-079).
   *
   * Dieselbe Form wie Zähler und Gerät, aus demselben Grund: Ein Feld, das nur
   * bei `area === "units"` etwas bedeuten darf, ließe sich überall sonst
   * mitschreiben, ohne dass es auffiele.
   */
  | {
      readonly kind: "unit";
      readonly siteId: string;
      readonly unitId: string;
      readonly tab: UnitTab;
      /** Zeitraum und Datum von „Strom" — wie bei „Strom & Erzeugung" (HM-080). */
      readonly period: EnergyPeriod;
      readonly date: string | null;
    }
  /**
   * **Die Wohnung eines Bewohners** — `/wohnung/{unitId}?zeitraum=&datum=` (HM-081).
   *
   * Weder Anlage noch Konto, wie die Rechtsseiten ein dritter Fall: Die Anlage
   * gehört dem Vermieter, und der Bewohner hat sie nicht; der Zeitraum steht
   * wie überall als Suchparameter.
   */
  | {
      readonly kind: "resident-unit";
      readonly unitId: string;
      /** `/wohnung/{id}` ist „Strom" (HM-156); `/wohnung/{id}/strom` wird weiter gedeutet. */
      readonly section: Exclude<UnitSection, "charging">;
      readonly period: EnergyPeriod;
      readonly date: string | null;
    }
  /**
   * **Laden in der Wohnung eines Bewohners** — `/wohnung/{unitId}/laden?ladepunkt=`
   * (HM-119). Dieselbe Form wie an der Anlage: kein Zeitraum, dafür der
   * gewählte Ladepunkt.
   */
  | {
      readonly kind: "resident-charging";
      readonly unitId: string;
      readonly chargePointId: string | null;
    }
  /**
   * Die Einladung eines Bewohners — `/einladung?token=` (HM-081), **ohne
   * Anmeldung** erreichbar wie die Adressen aus HM-026: Wer den Link aus der
   * Nachricht öffnet, hat oft noch kein Konto. Der Token steht als Parameter,
   * damit die Seite ihn nach dem Einlösen aus der Adresse nehmen kann.
   */
  | { readonly kind: "invitation"; readonly token: string | null }
  | { readonly kind: "account"; readonly area: Exclude<AccountArea, "usage" | "feedback" | "fleet">; readonly siteId: string | null }
  /**
   * **Alle Geräte der Plattform** — `/admin/geraete?zustand=&typ=` (#89). Eine
   * eigene Variante wie das Feedback: Zustand und Typ filtern nur hier, und
   * „alle nicht erreichbaren" soll sich verlinken lassen. Der Typ steht als
   * Schlüssel, wie ihn die API führt — er ist offen, eine Slug-Tabelle hinkte
   * jedem neuen Fremdgerät hinterher.
   */
  | {
      readonly kind: "fleet";
      readonly siteId: string | null;
      readonly state: FleetStateFilter | null;
      readonly deviceType: string | null;
    }
  /**
   * **Das Feedback aller Nutzer** — `/admin/feedback?art=&status=` und eine
   * Meldung darunter, `/admin/feedback/{id}` (HM-169). Nur für den
   * Plattform-Admin. Eine eigene Variante wie die Nutzungsstatistik: Die Filter
   * bedeuten nur hier etwas. Sie bleiben beim Öffnen einer Meldung erhalten,
   * damit „zurück" in derselben Auswahl landet.
   */
  | {
      readonly kind: "feedback";
      readonly siteId: string | null;
      readonly feedbackId: string | null;
      readonly feedbackKind: FeedbackKindFilter | null;
      readonly status: FeedbackStatusFilter | null;
    }
  /**
   * **Die Nutzungsstatistik** — `/admin/nutzung?tage=&plattform=` (HM-168).
   * Ein Konto-Bereich mit eigenen Parametern und deshalb eine eigene Variante,
   * aus demselben Grund wie „Strom & Erzeugung": Ein Feld, das nur hier etwas
   * bedeutet, ließe sich an jedem anderen Konto-Bereich mitschreiben. Zeitraum
   * und Plattform stellen die Ansicht ein und stehen als Suchparameter.
   */
  | {
      readonly kind: "usage";
      readonly siteId: string | null;
      readonly days: UsageDays;
      readonly platform: UsagePlatformFilter | null;
      readonly tab: UsageTab;
      readonly grain: UsageGrain;
    }
  /** Der Kopplungslink eines Geräts (`…/verbinden?code=…`, ADR 0018). */
  | { readonly kind: "claim"; readonly code: string | null }
  /**
   * Impressum, Datenschutzerklärung und Nutzungsbedingungen (HM-061).
   *
   * **Ohne Anmeldung erreichbar, und zwar zwingend.** Ein Impressum ist nach
   * § 5 DDG „ständig verfügbar" zu halten und eine Datenschutzerklärung nach
   * Art. 13 DSGVO **vor** der Erhebung zu geben — beides hinter einer
   * Anmeldung wäre keines von beidem. Dazu kommt der Haken bei der
   * Registrierung: Eine Einwilligung in einen Text, den man erst nach der
   * Einwilligung lesen kann, ist keine.
   */
  | { readonly kind: "legal"; readonly document: LegalDocument }
  /**
   * Die drei Adressen, die **ohne Sitzung** erreichbar sind (HM-026).
   *
   * Sie sind die einzige Ausnahme von „die Anmeldung hat keine eigene Adresse"
   * (siehe `app.tsx`): Wer sein Passwort vergessen hat, kommt gerade nicht
   * herein, und der Link aus einer E-Mail wird oft in einem Browser geöffnet,
   * in dem niemand angemeldet ist. Ein Ziel, das eine Anmeldung verlangt, um
   * eine Anmeldung zu ermöglichen, geht nicht auf.
   *
   * **Der Token steht als `?token=` und nicht im Pfad.** Er stellt die Ansicht
   * nicht ein, er ist ihr Gegenstand — aber ein Pfadsegment landete in jedem
   * Server-Protokoll als Teil der Adresse. Beides tut ein Suchparameter auch;
   * der Unterschied ist, dass ihn die Seite nach dem Einlösen aus der Adresse
   * entfernen kann, ohne die Route zu wechseln.
   */
  | { readonly kind: "forgot-password" }
  | { readonly kind: "reset-password"; readonly token: string | null }
  | { readonly kind: "verify-email"; readonly token: string | null };

/**
 * Das erste Segment eines Anlagen-Pfads. Nicht zu verwechseln mit `/konto/anlagen`
 * — das ist die Liste **aller** Anlagen und gehört dem Konto, während hier eine
 * einzelne gemeint ist. Beides ist eindeutig zu lesen, weil die Liste unter
 * `/konto/` steht.
 */
const SITE_PREFIX = "anlagen";
const ACCOUNT_PREFIX = "konto";
/**
 * Das erste Segment der Admin-Bereiche. Bis dahin standen sie unter `/konto/…`;
 * diese Adressen werden weiter **gedeutet, nicht gebildet** — der Rahmen
 * schreibt sie beim Zurechtrücken auf `/admin/…` um, wie die alten Reiter am
 * Gerät (HM-073).
 */
const ADMIN_PREFIX = "admin";

/** Die öffentlichen Adressen rund um Passwort und Adresse (HM-026). */
const FORGOT_PATH = "/passwort-vergessen";
const RESET_PATH = "/passwort-neu";
const VERIFY_PATH = "/adresse-bestaetigen";
const INVITATION_PATH = "/einladung";
const RESIDENT_PREFIX = "wohnung";
const TOKEN_PARAM = "token";

/**
 * Die Adressen der Rechtsseiten (HM-061).
 *
 * Deutsch wie alle anderen Pfade — und **die üblichen Wörter**: Wer ein
 * Impressum sucht, tippt `/impressum`, und ein Link aus einem Verzeichnis oder
 * einer alten E-Mail zeigt dorthin. `/rechtliches/impressum` wäre ordentlicher
 * sortiert und an keiner Stelle erwartet.
 */
const LEGAL_SLUGS: Record<LegalDocument, string> = {
  imprint: "impressum",
  privacy: "datenschutz",
  terms: "nutzungsbedingungen",
};

const SITE_SLUGS: Record<SiteSection, string> = {
  electricity: "strom",
  charging: "laden",
  meters: "zaehler",
  units: "wohneinheiten",
  /** Erzeugungsanlagen (HM-122): PV-Anlagen und Speicher hinter den Zählern. */
  generation: "erzeugungsanlagen",
  concept: "messkonzept",
  devices: "geraete",
  settings: "einstellungen",
};

const ACCOUNT_SLUGS: Record<AccountArea, string> = {
  profile: "profil",
  sites: "anlagen",
  system: "system",
  admin: "nutzer",
  /** Alle Geräte der Plattform für den Plattform-Admin (#89). */
  fleet: "geraete",
  /** Die Nutzungsstatistik für den Plattform-Admin (HM-168). */
  usage: "nutzung",
  /** Das Feedback aller Nutzer für den Plattform-Admin (HM-169). */
  feedback: "feedback",
};

const FEEDBACK_KIND_SLUGS: Record<FeedbackKindFilter, string> = { bug: "fehler", wish: "wunsch" };

/** Die Zustände der Geräteübersicht (#89) — `DEVICE_PRESENCE_STATES` aus der Domäne. */
export type FleetStateFilter = "reachable" | "readings_stalled" | "overdue" | "unreachable" | "never_seen";
const FLEET_STATE_SLUGS: Record<FleetStateFilter, string> = {
  reachable: "erreichbar",
  readings_stalled: "keine-werte",
  overdue: "ueberfaellig",
  unreachable: "nicht-erreichbar",
  never_seen: "nie-gemeldet",
};
const FLEET_STATE_PARAM = "zustand";
const FLEET_TYPE_PARAM = "typ";
/** Ein Gerätetyp ist ein opaker Schlüssel; was nicht danach aussieht, fällt weg. */
const DEVICE_TYPE_KEY = /^[a-z0-9_]{1,64}$/u;
const FEEDBACK_STATUS_SLUGS: Record<FeedbackStatusFilter, string> = {
  open: "offen",
  accepted: "uebernommen",
  done: "erledigt",
  rejected: "abgelehnt",
};

/**
 * Die Reiter am Gerät. **Die Übersicht hat keinen Slug**: Sie ist der Anfang,
 * und `/anlagen/{id}/geraete/{deviceId}` bleibt damit die Adresse, die die
 * Geräteliste seit HM-038 verlinkt — sonst zeigte jeder bestehende Link auf
 * einen Pfad, den der Rahmen im nächsten Augenblick umschriebe.
 */
const DEVICE_TAB_SLUGS: Record<Exclude<DeviceTab, "overview">, string> = {
  /** Nur an einem Ladepunkt (HM-119) — die Detailseite entscheidet, ob es ihn gibt. */
  control: "steuerung",
  settings: "einstellungen",
};

/**
 * **Die drei Slugs der alten Vierteilung gelten weiter** (HM-073). Sie standen
 * seit HM-042 in Lesezeichen und in jedem Link, den jemand verschickt hat; ein
 * `start` daraus schickte ihn auf die Übersicht der ersten Anlage — für einen
 * Pfad, der noch genau sagt, was gemeint war.
 *
 * **Gedeutet, nicht beibehalten.** Sie werden gelesen und nirgends gebildet:
 * `routePath` kennt nur `einstellungen`, und der Rahmen schreibt die Adresse
 * beim Zurechtrücken auf den neuen Slug um. Ein alter Link führt damit an die
 * richtige Stelle **und** hinterlässt die Adresse, die es künftig gibt.
 *
 * `datenpunkte` wird zur **Übersicht**, weil die Datenpunkte dorthin gewandert
 * sind; die beiden anderen zu den Einstellungen. Das ist keine Näherung,
 * sondern genau der Ort, an dem der jeweilige Inhalt jetzt steht.
 */
/**
 * Das Segment, unter dem ein **Datenpunkt** liegt:
 * `/…/geraete/{deviceId}/datenpunkte/{datapointId}`.
 *
 * **Eine eigene Konstante, seit „Datenpunkte" kein Reiter mehr ist** (HM-073).
 * Vorher stand hier `DEVICE_TAB_SLUGS.datapoints`, und die beiden Bedeutungen
 * fielen zufällig zusammen — mit den zwei Reitern hätte das die Adresse jedes
 * Datenpunkts mitgerissen. Sie bleibt unverändert: Die Datenpunkte sind in die
 * Übersicht gewandert, ihre **Verlaufsseite** liegt weiter hier.
 */
const DATAPOINT_SEGMENT = "datenpunkte";

const DEVICE_TAB_ALIASES: Record<string, DeviceTab> = {
  datenpunkte: "overview",
  konfiguration: "settings",
  aktionen: "settings",
};

/**
 * Die Reiter am Zähler. **Die Übersicht hat keinen Slug** — aus demselben Grund
 * wie beim Gerät: `/anlagen/{id}/zaehler/{meterId}` bleibt die Adresse, die die
 * Zählerliste seit HM-057 verlinkt.
 */
const METER_TAB_SLUGS: Record<Exclude<MeterTab, "overview">, string> = {
  settings: "einstellungen",
};

/** Der Suchparameter, an dem ein Konto-Bereich die gewählte Anlage mitführt. */
const SITE_PARAM = "anlage";
/** Und der, an dem ein Datenpunkt-Verlauf sein Zeitfenster mitführt. */
const RANGE_PARAM = "zeitraum";
const DATE_PARAM = "datum";
const PERIOD_SLUGS: Record<EnergyPeriod, string> = {
  day: "tag",
  week: "woche",
  month: "monat",
  year: "jahr",
};
const DEFAULT_PERIOD: EnergyPeriod = "day";
const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Welches Zählwerk eines Zählers die Kacheln und das Diagramm zeigen (HM-066). */
const REGISTER_PARAM = "zaehlwerk";
/** Zeitraum und Plattform der Nutzungsstatistik (HM-168). */
const USAGE_DAYS_PARAM = "tage";
const USAGE_PLATFORM_PARAM = "plattform";
const DEFAULT_USAGE_DAYS: UsageDays = 30;
const USAGE_GRAIN_PARAM = "raster";
const USAGE_TAB_SLUGS: Record<Exclude<UsageTab, "overview">, string> = {
  times: "zeiten",
  screens: "ansichten",
  actions: "funktionen",
  platforms: "plattformen",
};
/** Art und Status der Feedback-Liste (HM-169). */
const FEEDBACK_KIND_PARAM = "art";
const FEEDBACK_STATUS_PARAM = "status";
/** Welcher Ladepunkt auf „Laden" gewählt ist (HM-119). */
const CHARGE_POINT_PARAM = "ladepunkt";
const DEFAULT_RANGE: HistoryRange = "24h";

const LEGAL_DOCUMENTS = reverse(LEGAL_SLUGS);
const ACCOUNT_AREAS = reverse(ACCOUNT_SLUGS);
const FEEDBACK_KINDS_BY_SLUG = reverse(FEEDBACK_KIND_SLUGS);
const FLEET_STATES_BY_SLUG = reverse(FLEET_STATE_SLUGS);
const USAGE_TABS_BY_SLUG = reverse(USAGE_TAB_SLUGS);
const FEEDBACK_STATUSES_BY_SLUG = reverse(FEEDBACK_STATUS_SLUGS);
const DEVICE_TABS: Record<string, DeviceTab | undefined> = {
  ...DEVICE_TAB_ALIASES,
  ...reverse(DEVICE_TAB_SLUGS),
};
const METER_TABS = reverse(METER_TAB_SLUGS);
/**
 * **Die Übersicht ist entfallen, ihre Adressen gelten weiter** (HM-156). Sie
 * standen seit HM-030 in Lesezeichen und als Ziel jedes Anlagenwechsels;
 * `uebersicht` wird zu „Strom & Erzeugung" — derselben Frage an dasselbe
 * Messkonzept, ausführlicher beantwortet — und der Rahmen schreibt die
 * Adresse beim Zurechtrücken um. Gedeutet, nicht gebildet, wie die alten
 * Reiter am Gerät (HM-073).
 */
const SITE_SECTION_ALIASES: Record<string, SiteSection> = {
  uebersicht: "electricity",
};

/** Die Reiter an der Wohneinheit — „Strom" ohne Slug, seit die Übersicht entfallen ist. */
const UNIT_TAB_SLUGS: Record<Exclude<UnitTab, "electricity">, string> = {
  residents: "bewohner",
  settings: "einstellungen",
};
/** Die Sparten-Ansichten einer Wohnung — „Strom" ohne Slug, wie an der Wohneinheit. */
const UNIT_SECTION_SLUGS: Record<Exclude<UnitSection, "electricity">, string> = {
  charging: "laden",
};
/** `…/strom` war bis HM-156 der Slug der Sparte und wird weiter gedeutet. */
const UNIT_SECTIONS: Record<string, UnitSection | undefined> = {
  strom: "electricity",
  ...reverse(UNIT_SECTION_SLUGS),
};
const UNIT_TABS: Record<string, UnitTab | undefined> = {
  strom: "electricity",
  ...reverse(UNIT_TAB_SLUGS),
};
const SITE_SECTIONS: Record<string, SiteSection | undefined> = {
  ...SITE_SECTION_ALIASES,
  ...reverse(SITE_SLUGS),
};
const PERIODS = reverse(PERIOD_SLUGS);

/**
 * Ob ein Bereich der Anlage gehört.
 *
 * **Das ist die einzige Quelle für diese Frage.** Früher stand sie zusätzlich als
 * `scope` an jedem Bereich — zwei Listen, die dasselbe sagen, laufen irgendwann
 * auseinander. Hier trägt der Typ die Antwort, und die Adresse folgt ihr.
 */
export function isSiteSection(area: Area): area is SiteSection {
  return area in SITE_SLUGS;
}

/** Ob ein Bereich dem Plattform-Admin gehört — die einzige Stelle für diese Frage. */
export function isAdminArea(area: Area): area is AdminArea {
  return area === "system" || area === "admin" || area === "fleet" || area === "usage" || area === "feedback";
}

function areaPrefix(area: AccountArea): string {
  return isAdminArea(area) ? ADMIN_PREFIX : ACCOUNT_PREFIX;
}

/**
 * Liest eine Adresse.
 *
 * **Was nicht zu deuten ist, wird nicht geraten** (Regel 9): Ein unbekannter Pfad
 * ergibt `start` und damit dieselbe Behandlung wie ein Aufruf der Wurzel — der
 * Rahmen schreibt eine gültige Adresse. Aus `/anlagen/{id}/quatsch` „Strom"
 * zu machen, hieße dem Nutzer zu unterstellen, er habe sie gemeint.
 */
export function parseRoute(pathname: string, search = ""): Route {
  if (pathname === CLAIM_PATH) {
    return { kind: "claim", code: parseClaimCode(new URLSearchParams(search).get("code")) };
  }

  const legalDocument = LEGAL_DOCUMENTS[pathname.replace(/^\//, "")];
  if (legalDocument !== undefined) return { kind: "legal", document: legalDocument };

  if (pathname === FORGOT_PATH) return { kind: "forgot-password" };
  if (pathname === RESET_PATH) {
    return { kind: "reset-password", token: parseToken(new URLSearchParams(search)) };
  }
  if (pathname === VERIFY_PATH) {
    return { kind: "verify-email", token: parseToken(new URLSearchParams(search)) };
  }
  if (pathname === INVITATION_PATH) {
    return { kind: "invitation", token: parseToken(new URLSearchParams(search)) };
  }

  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  const siteId = new URLSearchParams(search).get(SITE_PARAM);

  if (segments.length === 3 && segments[0] === SITE_PREFIX) {
    const area = SITE_SECTIONS[segments[2] ?? ""];
    const id = decodeURIComponent(segments[1] ?? "");
    if (area === "charging" && id.length > 0) {
      return { kind: "charging", siteId: id, chargePointId: parseChargePoint(search) };
    }
    if (area === "electricity" && id.length > 0) {
      const params = new URLSearchParams(search);
      const date = params.get(DATE_PARAM);
      return {
        kind: "electricity",
        siteId: id,
        period: PERIODS[params.get(RANGE_PARAM) ?? ""] ?? DEFAULT_PERIOD,
        /* Ein Datum, das keines ist, bedeutet dasselbe wie kein Datum: der
           aktuelle Zeitraum. Ein Tippfehler in der Adresse soll auf die
           übliche Ansicht fallen, nicht auf eine leere. */
        date: date !== null && LOCAL_DATE.test(date) ? date : null,
      };
    }
    /* Die beiden Bereiche mit eigenen Parametern sind oben behandelt; hier
       bleiben die übrigen — der Typ von `Route.site` schließt sie aus. */
    if (
      area !== undefined &&
      area !== "electricity" &&
      area !== "charging" &&
      id.length > 0
    ) {
      return { kind: "site", siteId: id, area };
    }
  }

  /**
   * Ein Gerät liegt **unter** seiner Geräteverwaltung, nicht daneben. Der Pfad
   * sagt damit auch, wohin „zurück" führt — und dass ein Gerät ohne Anlage keine
   * Adresse hat (ADR 0017).
   */
  /**
   * Ein Datenpunkt liegt unter dem Reiter, aus dem er kommt — sechs Segmente,
   * das fünfte ist der Reiter-Slug selbst. Damit ist `…/datenpunkte` die Liste
   * und `…/datenpunkte/{id}` einer daraus; ein Pfad, der beides in derselben
   * Länge ausdrückte, ließe sich nicht auseinanderhalten.
   */
  if (
    segments.length === 6 &&
    segments[0] === SITE_PREFIX &&
    segments[2] === SITE_SLUGS.devices &&
    segments[4] === DATAPOINT_SEGMENT
  ) {
    const siteId = decodeURIComponent(segments[1] ?? "");
    const deviceId = decodeURIComponent(segments[3] ?? "");
    const datapointId = decodeURIComponent(segments[5] ?? "");

    if (siteId.length > 0 && deviceId.length > 0 && datapointId.length > 0) {
      return {
        kind: "datapoint",
        siteId,
        deviceId,
        datapointId,
        range: parseRange(new URLSearchParams(search).get(RANGE_PARAM)),
      };
    }
  }

  if (
    (segments.length === 4 || segments.length === 5) &&
    segments[0] === SITE_PREFIX &&
    segments[2] === SITE_SLUGS.meters
  ) {
    const siteId = decodeURIComponent(segments[1] ?? "");
    const meterId = decodeURIComponent(segments[3] ?? "");
    /**
     * Ein fünftes Segment, das kein Reiter ist, ergibt **nicht** die Übersicht
     * — dieselbe Strenge wie beim Gerät: `/…/m-4/quatsch` als Zähler zu lesen,
     * hieße dem Nutzer zu unterstellen, er habe ihn gemeint (Regel 9).
     */
    const tab: MeterTab | undefined =
      segments.length === 4 ? "overview" : METER_TABS[segments[4] ?? ""];

    if (siteId.length > 0 && meterId.length > 0 && tab !== undefined) {
      const params = new URLSearchParams(search);
      const register = params.get(REGISTER_PARAM);
      return {
        kind: "meter",
        siteId,
        meterId,
        tab,
        /* Ein leerer Parameter ist keine Kennung, sondern ein Tippfehler in der
           Adresse — er bedeutet dasselbe wie kein Parameter. */
        register: register !== null && register.length > 0 ? register : null,
        range: parseRange(params.get(RANGE_PARAM)),
      };
    }
  }

  if (segments.length === 4 && segments[0] === SITE_PREFIX && segments[2] === SITE_SLUGS.generation) {
    const siteId = decodeURIComponent(segments[1] ?? "");
    const assetId = decodeURIComponent(segments[3] ?? "");
    if (siteId.length > 0 && assetId.length > 0) {
      return {
        kind: "generation-asset",
        siteId,
        assetId,
        range: parseRange(new URLSearchParams(search).get(RANGE_PARAM)),
      };
    }
  }

  if (
    (segments.length === 4 || segments.length === 5) &&
    segments[0] === SITE_PREFIX &&
    segments[2] === SITE_SLUGS.units
  ) {
    const siteId = decodeURIComponent(segments[1] ?? "");
    const unitId = decodeURIComponent(segments[3] ?? "");
    const tab: UnitTab | undefined = segments.length === 4 ? "electricity" : UNIT_TABS[segments[4] ?? ""];
    if (siteId.length > 0 && unitId.length > 0 && tab !== undefined) {
      const params = new URLSearchParams(search);
      const date = params.get(DATE_PARAM);
      return {
        kind: "unit",
        siteId,
        unitId,
        tab,
        period: PERIODS[params.get(RANGE_PARAM) ?? ""] ?? DEFAULT_PERIOD,
        date: date !== null && LOCAL_DATE.test(date) ? date : null,
      };
    }
  }

  if (
    (segments.length === 4 || segments.length === 5) &&
    segments[0] === SITE_PREFIX &&
    segments[2] === SITE_SLUGS.devices
  ) {
    const siteId = decodeURIComponent(segments[1] ?? "");
    const deviceId = decodeURIComponent(segments[3] ?? "");
    /**
     * Ein fünftes Segment, das kein Reiter ist, ergibt **nicht** die Übersicht:
     * `/…/dev-9/quatsch` als Gerät zu lesen, hieße dem Nutzer zu unterstellen,
     * er habe es gemeint (Regel 9).
     */
    const tab: DeviceTab | undefined =
      segments.length === 4 ? "overview" : DEVICE_TABS[segments[4] ?? ""];

    if (siteId.length > 0 && deviceId.length > 0 && tab !== undefined) {
      return { kind: "device", siteId, deviceId, tab };
    }
  }

  if ((segments.length === 2 || segments.length === 3) && segments[0] === RESIDENT_PREFIX) {
    const unitId = decodeURIComponent(segments[1] ?? "");
    const section: UnitSection | undefined = segments.length === 2 ? "electricity" : UNIT_SECTIONS[segments[2] ?? ""];
    if (unitId.length > 0 && section === "charging") {
      return { kind: "resident-charging", unitId, chargePointId: parseChargePoint(search) };
    }
    if (unitId.length > 0 && section !== undefined && section !== "charging") {
      const params = new URLSearchParams(search);
      const date = params.get(DATE_PARAM);
      return {
        kind: "resident-unit",
        unitId,
        section,
        period: PERIODS[params.get(RANGE_PARAM) ?? ""] ?? DEFAULT_PERIOD,
        date: date !== null && LOCAL_DATE.test(date) ? date : null,
      };
    }
  }

  /* Unter `/admin/` nur die Admin-Bereiche; unter `/konto/` alle — dort stehen
     die Admin-Bereiche als alte Adressen, die weiter gedeutet werden. */
  const accountArea =
    segments[0] === ACCOUNT_PREFIX || segments[0] === ADMIN_PREFIX ? ACCOUNT_AREAS[segments[1] ?? ""] : undefined;
  const inAccountTree =
    accountArea !== undefined && (segments[0] === ACCOUNT_PREFIX || isAdminArea(accountArea));

  if ((segments.length === 2 || segments.length === 3) && inAccountTree && accountArea === "feedback") {
    const params = new URLSearchParams(search);
    return {
      kind: "feedback",
      siteId,
      feedbackId: segments[2] !== undefined ? decodeURIComponent(segments[2]) : null,
      /* Ein unbekannter Filter stellt die Liste nur ein — er fällt weg. */
      feedbackKind: FEEDBACK_KINDS_BY_SLUG[params.get(FEEDBACK_KIND_PARAM) ?? ""] ?? null,
      status: FEEDBACK_STATUSES_BY_SLUG[params.get(FEEDBACK_STATUS_PARAM) ?? ""] ?? null,
    };
  }

  if (segments.length === 2 && inAccountTree && accountArea === "fleet") {
    const params = new URLSearchParams(search);
    const type = params.get(FLEET_TYPE_PARAM);
    return {
      kind: "fleet",
      siteId,
      /* Ein unbekannter Filter stellt die Liste nur ein — er fällt weg. */
      state: FLEET_STATES_BY_SLUG[params.get(FLEET_STATE_PARAM) ?? ""] ?? null,
      deviceType: type !== null && DEVICE_TYPE_KEY.test(type) ? type : null,
    };
  }

  const usageTab: UsageTab | undefined =
    segments.length === 2 ? "overview" : USAGE_TABS_BY_SLUG[segments[2] ?? ""];
  if ((segments.length === 2 || segments.length === 3) && inAccountTree && accountArea === "usage" && usageTab !== undefined) {
    const params = new URLSearchParams(search);
    const days = Number(params.get(USAGE_DAYS_PARAM));
    const platform = params.get(USAGE_PLATFORM_PARAM);
    return {
      kind: "usage",
      siteId,
      /* Ein unbekannter Zeitraum stellt die Ansicht nur ein — er fällt auf die
         Vorgabe, wie beim Verlaufsfenster eines Datenpunkts. */
      days: USAGE_DAYS.find((value) => value === days) ?? DEFAULT_USAGE_DAYS,
      platform: USAGE_PLATFORM_FILTERS.find((value) => value === platform) ?? null,
      tab: usageTab,
      grain: params.get(USAGE_GRAIN_PARAM) === "woche" ? "week" : "day",
    };
  }

  if (segments.length === 2 && inAccountTree) {
    const area = accountArea;
    if (area !== undefined && area !== "feedback" && area !== "usage" && area !== "fleet") return { kind: "account", area, siteId };
  }

  return { kind: "start" };
}

/**
 * **Ein unbekanntes Zeitfenster wird nicht geraten, sondern durch das übliche
 * ersetzt** (Regel 9). Anders als bei einem unbekannten Pfad ist das hier
 * richtig: Der Parameter stellt die Ansicht nur ein — was sie zeigt, steht im
 * Pfad und bleibt gültig.
 */
function parseRange(value: string | null): HistoryRange {
  return HISTORY_RANGES.find((range) => range === value) ?? DEFAULT_RANGE;
}

/** Ein leerer Parameter ist keine Kennung — er heißt dasselbe wie keiner: der erste Ladepunkt. */
function parseChargePoint(search: string): string | null {
  const value = new URLSearchParams(search).get(CHARGE_POINT_PARAM);
  return value !== null && value.length > 0 ? value : null;
}

function withChargePoint(path: string, chargePointId: string | null): string {
  if (chargePointId === null) return path;
  return `${path}?${new URLSearchParams({ [CHARGE_POINT_PARAM]: chargePointId }).toString()}`;
}

/** Bildet die Adresse zu einer Route — die Umkehrung von `parseRoute`. */
export function routePath(route: Route): string {
  switch (route.kind) {
    case "site":
      return `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS[route.area]}`;
    case "charging":
      return withChargePoint(
        `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS.charging}`,
        route.chargePointId,
      );
    case "electricity": {
      const path = `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS.electricity}`;
      // Die Vorgabe steht nicht in der Adresse: `?zeitraum=tag` an jedem Link
      // sagt nichts, was das Fehlen nicht auch sagte.
      const params = new URLSearchParams();
      if (route.period !== DEFAULT_PERIOD) params.set(RANGE_PARAM, PERIOD_SLUGS[route.period]);
      if (route.date !== null) params.set(DATE_PARAM, route.date);
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    }
    case "device": {
      const path =
        `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS.devices}` +
        `/${encodeURIComponent(route.deviceId)}`;
      return route.tab === "overview" ? path : `${path}/${DEVICE_TAB_SLUGS[route.tab]}`;
    }
    case "datapoint": {
      const path =
        `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS.devices}` +
        `/${encodeURIComponent(route.deviceId)}/${DATAPOINT_SEGMENT}` +
        `/${encodeURIComponent(route.datapointId)}`;
      // Der Vorgabewert steht nicht in der Adresse: `?zeitraum=24h` an jedem Link
      // sagt nichts, was das Fehlen nicht auch sagte.
      return route.range === DEFAULT_RANGE
        ? path
        : `${path}?${new URLSearchParams({ [RANGE_PARAM]: route.range }).toString()}`;
    }
    case "meter": {
      const base =
        `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS.meters}` +
        `/${encodeURIComponent(route.meterId)}`;
      const path = route.tab === "overview" ? base : `${base}/${METER_TAB_SLUGS[route.tab]}`;
      // Wie beim Datenpunkt: Der Vorgabewert steht nicht in der Adresse.
      const params = new URLSearchParams();
      if (route.register !== null) params.set(REGISTER_PARAM, route.register);
      if (route.range !== DEFAULT_RANGE) params.set(RANGE_PARAM, route.range);
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    }
    case "generation-asset": {
      const path =
        `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS.generation}` +
        `/${encodeURIComponent(route.assetId)}`;
      // Wie beim Datenpunkt: Der Vorgabewert steht nicht in der Adresse.
      return route.range === DEFAULT_RANGE
        ? path
        : `${path}?${new URLSearchParams({ [RANGE_PARAM]: route.range }).toString()}`;
    }
    case "unit": {
      const base =
        `/${SITE_PREFIX}/${encodeURIComponent(route.siteId)}/${SITE_SLUGS.units}` +
        `/${encodeURIComponent(route.unitId)}`;
      const path = route.tab === "electricity" ? base : `${base}/${UNIT_TAB_SLUGS[route.tab]}`;
      const params = new URLSearchParams();
      if (route.period !== DEFAULT_PERIOD) params.set(RANGE_PARAM, PERIOD_SLUGS[route.period]);
      if (route.date !== null) params.set(DATE_PARAM, route.date);
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    }
    case "account": {
      const path = `/${areaPrefix(route.area)}/${ACCOUNT_SLUGS[route.area]}`;
      // Ohne gewählte Anlage kein leerer Parameter: `?anlage=` sähe aus wie eine
      // Anlage ohne Kennung.
      if (route.siteId === null) return path;
      return `${path}?${new URLSearchParams({ [SITE_PARAM]: route.siteId }).toString()}`;
    }
    case "usage": {
      const base = `/${ADMIN_PREFIX}/${ACCOUNT_SLUGS.usage}`;
      const path = route.tab === "overview" ? base : `${base}/${USAGE_TAB_SLUGS[route.tab]}`;
      const params = new URLSearchParams();
      if (route.siteId !== null) params.set(SITE_PARAM, route.siteId);
      if (route.days !== DEFAULT_USAGE_DAYS) params.set(USAGE_DAYS_PARAM, String(route.days));
      if (route.platform !== null) params.set(USAGE_PLATFORM_PARAM, route.platform);
      if (route.grain === "week") params.set(USAGE_GRAIN_PARAM, "woche");
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    }
    case "feedback": {
      const base = `/${ADMIN_PREFIX}/${ACCOUNT_SLUGS.feedback}`;
      const path = route.feedbackId === null ? base : `${base}/${encodeURIComponent(route.feedbackId)}`;
      const params = new URLSearchParams();
      if (route.siteId !== null) params.set(SITE_PARAM, route.siteId);
      if (route.feedbackKind !== null) params.set(FEEDBACK_KIND_PARAM, FEEDBACK_KIND_SLUGS[route.feedbackKind]);
      if (route.status !== null) params.set(FEEDBACK_STATUS_PARAM, FEEDBACK_STATUS_SLUGS[route.status]);
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    }
    case "fleet": {
      const path = `/${ADMIN_PREFIX}/${ACCOUNT_SLUGS.fleet}`;
      const params = new URLSearchParams();
      if (route.siteId !== null) params.set(SITE_PARAM, route.siteId);
      if (route.state !== null) params.set(FLEET_STATE_PARAM, FLEET_STATE_SLUGS[route.state]);
      if (route.deviceType !== null) params.set(FLEET_TYPE_PARAM, route.deviceType);
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    }
    case "resident-unit": {
      const base = `/${RESIDENT_PREFIX}/${encodeURIComponent(route.unitId)}`;
      const path = base;
      const params = new URLSearchParams();
      if (route.period !== DEFAULT_PERIOD) params.set(RANGE_PARAM, PERIOD_SLUGS[route.period]);
      if (route.date !== null) params.set(DATE_PARAM, route.date);
      const query = params.toString();
      return query.length === 0 ? path : `${path}?${query}`;
    }
    case "resident-charging":
      return withChargePoint(
        `/${RESIDENT_PREFIX}/${encodeURIComponent(route.unitId)}/${UNIT_SECTION_SLUGS.charging}`,
        route.chargePointId,
      );
    case "invitation":
      return withToken(INVITATION_PATH, route.token);
    case "claim":
      return route.code === null ? CLAIM_PATH : `${CLAIM_PATH}?code=${route.code}`;
    case "legal":
      return `/${LEGAL_SLUGS[route.document]}`;
    case "forgot-password":
      return FORGOT_PATH;
    case "reset-password":
      return withToken(RESET_PATH, route.token);
    case "verify-email":
      return withToken(VERIFY_PATH, route.token);
    case "start":
      return "/";
  }
}

/**
 * Der Token aus der Adresse.
 *
 * Leer ist wie nicht vorhanden: `?token=` entsteht beim Kopieren eines Links
 * ebenso leicht wie beim Tippen, und ein leerer Token soll dieselbe Seite
 * zeigen wie gar keiner — die, die sagt, dass der Link unvollständig ist.
 */
function parseToken(params: URLSearchParams): string | null {
  const value = params.get(TOKEN_PARAM);
  return value !== null && value.length > 0 ? value : null;
}

function withToken(path: string, token: string | null): string {
  if (token === null) return path;
  return `${path}?${new URLSearchParams({ [TOKEN_PARAM]: token }).toString()}`;
}

/**
 * Die Adresse, die zu einem Bereich und einer Anlage gehört.
 *
 * Ein Anlagen-Bereich **ohne** Anlage ist keine Adresse: Es gibt nichts, worauf er
 * zeigen könnte. Er führt deshalb zur Anlagenliste — dieselbe Umlenkung, die der
 * Rahmen für die Anzeige vornimmt, nur hier für den Pfad.
 */
export function routeFor(area: Area, siteId: string | null): Route {
  if (isSiteSection(area)) {
    if (siteId === null) return { kind: "account", area: "sites", siteId: null };
    if (area === "electricity") {
      return { kind: "electricity", siteId, period: DEFAULT_PERIOD, date: null };
    }
    if (area === "charging") return { kind: "charging", siteId, chargePointId: null };
    return { kind: "site", siteId, area };
  }

  if (area === "usage") {
    return { kind: "usage", siteId, days: DEFAULT_USAGE_DAYS, platform: null, tab: "overview", grain: "day" };
  }
  if (area === "feedback") return { kind: "feedback", siteId, feedbackId: null, feedbackKind: null, status: null };
  if (area === "fleet") return { kind: "fleet", siteId, state: null, deviceType: null };
  return { kind: "account", area, siteId };
}

/**
 * Dieselbe Ansicht, aber unter einer anderen Anlage.
 *
 * **Für den Rahmen, der eine erloschene Kennung ersetzt.** Zeigt die Adresse auf
 * eine Anlage, die es nicht mehr gibt, wird sie durch die gewählte ersetzt — und
 * das geöffnete Gerät oder der geöffnete Datenpunkt wandert mit, statt auf die
 * Liste zurückzufallen. Ob es dorthin gehört, entscheidet die Detailseite, die
 * als Einzige das Gerät kennt.
 */
export function withSite(route: Route, siteId: string): Route {
  switch (route.kind) {
    case "site":
    case "electricity":
    case "charging":
    case "device":
    case "datapoint":
    case "meter":
    case "generation-asset":
    case "unit":
      return { ...route, siteId };
    default:
      return route;
  }
}

/* --------------------------------------------------------------------------
 * Der Zustand der Adressleiste
 * ----------------------------------------------------------------------- */

const listeners = new Set<() => void>();

/**
 * Der zuletzt gelesene Pfad samt geparster Route.
 *
 * React vergleicht den Schnappschuss mit `Object.is`. Bei jedem Aufruf neu zu
 * parsen ergäbe jedes Mal ein neues Objekt und damit eine Endlosschleife — wie
 * beim Sitzungszustand in `session.ts`.
 */
let snapshotHref: string | undefined;
let snapshotRoute: Route = { kind: "start" };

function href(): string {
  return window.location.pathname + window.location.search;
}

/** Die Route, auf der die Anwendung gerade steht. */
export function currentRoute(): Route {
  const now = href();
  if (now !== snapshotHref) {
    snapshotHref = now;
    snapshotRoute = parseRoute(window.location.pathname, window.location.search);
  }
  return snapshotRoute;
}

function subscribeRoute(listener: () => void): () => void {
  listeners.add(listener);
  // `popstate` deckt die Zurück- und Vorwärts-Taste ab; `navigate` meldet sich
  // selbst, weil `pushState` kein Ereignis auslöst.
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

/**
 * Wechselt die Adresse.
 *
 * `replace` für alles, was den Nutzer **nicht** aktiv dorthin geführt hat — das
 * Zurechtrücken einer unvollständigen Adresse etwa. Sonst sammelte die
 * Zurück-Taste Schritte ein, die niemand gegangen ist, und führte zurück auf eine
 * Adresse, die sofort wieder zurechtgerückt würde.
 */
export function navigate(route: Route, options: { readonly replace?: boolean } = {}): void {
  const target = routePath(route);
  // Dieselbe Adresse noch einmal zu setzen, erzeugte einen Eintrag im Verlauf,
  // der nirgendwohin führt.
  if (target === href()) return;

  window.history[options.replace === true ? "replaceState" : "pushState"](null, "", target);
  for (const listener of listeners) listener();
}

/** Die aktuelle Route als React-Zustand. */
export function useRoute(): Route {
  return useSyncExternalStore(subscribeRoute, currentRoute, () => snapshotRoute);
}

function reverse<A extends string>(slugs: Record<A, string>): Record<string, A | undefined> {
  return Object.fromEntries(
    Object.entries(slugs).map(([area, slug]) => [slug as string, area as A]),
  );
}
